import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";
import { Box, Typography, Button, Chip } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { getWipTrackers } from "../../reducers/wip.reducer";

import WIPUploadModal from "./WIPUploadModal.component";

import { LicenseInfo } from "@mui/x-license";
LicenseInfo.setLicenseKey(
  "fd65fb21e6b06577b510cbbfe2cc8f3dTz0xMTMxNDIsRT0xNzkzNjYzOTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y"
);

const WIPDataGrid = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Get data from Redux
  const trackers = useSelector((state) => state.wip.wipItems);
  const loading = useSelector((state) => state.wip.loading);

  // Load trackers on mount
  useEffect(() => {
    dispatch(getWipTrackers({}));
  }, [dispatch]);

  const rows = useMemo(() => {
    return trackers.map((item) => {
      // Data is nested in mainInformation object
      const mainInfo = item.mainInformation || {};

      return {
        id: item._id,
        factoryName: item.factoryName || mainInfo.Factory || "Unknown",
        supplierName: item?.supplierName || "Unknown",
        category: mainInfo.CATEGORY || "-",
        vendorStyle: mainInfo["VENDOR STYLE #"] || "-",
        rootsStyle: mainInfo["ROOTS STYLE #"] || "-",
        poNumber: mainInfo["PO #"] || "-",
        season: item.season || "-",
        colour: mainInfo.COLOUR || "-",
        customer: mainInfo["CUSTOMER (Select from drop down)"] || "-",
        units: parseInt(mainInfo.UNITS, 10) || 0, // Convert to number
        description: mainInfo.DESCRIPTION || "-",
        dateAdded: item.dateAdded || item.createdAt || item.uploadedAt,
        statusRollup: item.statusRollup,
      };
    });
  }, [trackers]);

  console.log("trackers", trackers);
  const columns = [
    {
      field: "factoryName",
      headerName: "Factory",
      flex: 1.5,
      minWidth: 180,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      flex: 1.5,
      minWidth: 180,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "category",
      headerName: "Category",
      flex: 0.8,
      minWidth: 100,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "vendorStyle",
      headerName: "Vendor Style #",
      flex: 1,
      minWidth: 130,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "rootsStyle",
      headerName: "Roots Style #",
      flex: 1,
      minWidth: 130,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "poNumber",
      headerName: "PO #",
      flex: 1,
      minWidth: 110,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "season",
      headerName: "Season",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "colour",
      headerName: "Colour",
      flex: 1,
      minWidth: 110,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 120,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "units",
      headerName: "Units",
      flex: 0.7,
      minWidth: 80,
      align: "right",
      headerAlign: "right",
      valueFormatter: (params) => {
        if (!params || !params.value) return "0";
        return params.value.toLocaleString();
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 180,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "dateAdded",
      headerName: "Date Added",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params) => {
        if (!params || !params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      },
    },
    {
      field: "details",
      headerName: "",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Button
            variant="contained"
            onClick={() => {
              // Navigate to details view with item ID (trackerId)
              // This will trigger the saga to fetch the specific item
              const queryParams = new URLSearchParams();
              queryParams.append("trackerId", params.row.id);
              navigate(`/wip/details?${queryParams.toString()}`);
            }}
            sx={{
              backgroundColor: "#4B68D4",
              color: "white",
              "&:hover": {
                backgroundColor: "#3A54B6",
              },
              textTransform: "none",
              width: "79px",
              height: "40px",
              borderRadius: "8px",
            }}
            size="small"
          >
            Details
          </Button>
        );
      },
    },
  ];

  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
    // Reload trackers after upload
    dispatch(getWipTrackers({}));
  };

  return (
    <Box sx={{ px: 3 }}>
      <Box sx={{ width: "100%", padding: "0px 48px", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
            marginTop: "30px",
          }}
        >
          <Typography
            style={{
              fontSize: 30,
              fontWeight: 700,
            }}
          >
            WIP Trackers
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              disabled={selectedRows.length === 0}
              onClick={() => {
                // Navigate to the first selected tracker as demo
                const trackerId = selectedRows[0];
                const tracker = trackers.find((t) => t._id === trackerId);
                if (tracker && tracker.factoryId) {
                  const queryParams = new URLSearchParams();
                  queryParams.append("factoryId", tracker.factoryId);
                  navigate(`/wip/details?${queryParams.toString()}`);
                } else {
                  navigate(`/wip/${trackerId}`);
                }
              }}
              sx={{
                backgroundColor: "#4B68D4",
                color: "white",
                textTransform: "none",
                borderRadius: "12px",
                px: 2.5,
                py: 1,
                fontWeight: 500,
                boxShadow: "0 2px 8px rgba(75, 104, 212, 0.25)",
                "&:hover": {
                  backgroundColor: "#3A54B6",
                  boxShadow: "0 4px 12px rgba(75, 104, 212, 0.35)",
                  transform: "translateY(-1px)",
                  transition: "all 0.2s ease-in-out",
                },
                "&:disabled": {
                  boxShadow: "none",
                },
              }}
            >
              Open Selected Tracker in One Page
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadModalOpen(true)}
              sx={{
                backgroundColor: "#4B68D4",
                color: "white",
                textTransform: "none",
                borderRadius: "12px",
                px: 2.5,
                py: 1,
                fontWeight: 500,
                boxShadow: "0 2px 8px rgba(75, 104, 212, 0.25)",
                "&:hover": {
                  backgroundColor: "#3A54B6",
                  boxShadow: "0 4px 12px rgba(75, 104, 212, 0.35)",
                  transform: "translateY(-1px)",
                  transition: "all 0.2s ease-in-out",
                },
              }}
            >
              Upload new WIP data
            </Button>
          </Box>
        </Box>

        {/* Upload Modal */}
        <WIPUploadModal
          open={uploadModalOpen}
          onClose={handleUploadModalClose}
        />

        <div style={{ height: "calc(100vh - 350px)", width: "100%" }}>
          <DataGridPremium
            columns={columns}
            rows={rows}
            loading={loading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
            }}
            disableColumnSelector
            disableDensitySelector
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            sx={{
              borderRadius: "8px",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f0f0f0",
                fontWeight: "500",
              },
            }}
          />
        </div>
      </Box>
    </Box>
  );
};

export default WIPDataGrid;
